import { Injectable } from '@angular/core';
import { ExecutionResult, LcsApiService } from '@lcs/shared/api';
import { OrderingOption } from 'generated/graphql-types';
import { Observable } from 'rxjs';
import { FilterExpression, FilterOption, serializeFilterExpression } from 'src/app/filters/filter-definition';
import { FilterOperations } from 'src/app/filters/filter-operation/filter-operations.enum';
import { FilterPrimitive } from 'src/app/filters/filter-primitives';
import { LogicalOperators } from 'src/app/filters/logical-operators.enum';
import { CallParkingLotFilterOptions } from '../models/call-parking-filter-option';
import { CallParkingRegisterOptions } from '../models/call-parkinglot-register-options';
import { getCallLogsQuery } from '../queries/call-parkinglot.graphql';
import { GetCallParkingLots, GetCallParkingLotsVariables } from '../queries/generated/GetCallParkingLots';

@Injectable({ providedIn: 'root' })
export class CallParkingLotRegisterApiService {
   constructor(private apiService: LcsApiService) {}

   getParkingLots(
      endCursor: string | null,
      filters: CallParkingLotFilterOptions[] | null,
      quickFilters: CallParkingRegisterOptions | null,
      orderingOptions: OrderingOption[] | null
   ): Observable<ExecutionResult<GetCallParkingLots>> {
      const filterBuilderFilterExpression: FilterExpression | null =
         filters && filters.length > 0
            ? {
                 filterOptions: filters.map(this.convertCallParkingLotFilterOption.bind(this)),
              }
            : { filterOptions: [] };

      const variables = this.getCallLogsVariables(endCursor, filterBuilderFilterExpression, orderingOptions);
      return this.apiService.query<GetCallParkingLots, GetCallParkingLotsVariables>({
         query: getCallLogsQuery,
         variables,
      });
   }

   private getCallLogsVariables(
      after: string | null,
      filterBuilderFilters: FilterExpression | null,
      orderingOptions: OrderingOption[] | null
   ): GetCallParkingLotsVariables {
      const variables: GetCallParkingLotsVariables = {
         after,
         filters: this.buildFilterExpression(filterBuilderFilters),
         first: 50,
         orderingOptions,
      };
      return variables;
   }

   private buildFilterExpression(filterBuilderFilters: FilterExpression | null): string {
      const expressions: FilterExpression[] = filterBuilderFilters?.subExpressions ?? [];

      if (filterBuilderFilters) {
         return serializeFilterExpression({
            filterOptions: filterBuilderFilters.filterOptions,
            logicalOperator: LogicalOperators.And,
            subExpressions: expressions,
         });
      }

      if (expressions.length > 1) {
         return serializeFilterExpression({
            filterOptions: [],
            logicalOperator: LogicalOperators.And,
            subExpressions: expressions,
         });
      } else if (expressions.length === 1) {
         return serializeFilterExpression(expressions[0]);
      } else {
         return '';
      }
   }

   private convertCallParkingLotFilterOption(option: CallParkingLotFilterOptions): FilterOption {
      return {
         field: option.field,
         operation: option.operation,
         value: this.getFilterOptionValue(option),
      };
   }

   private getFilterOptionValue(option: CallParkingLotFilterOptions): FilterPrimitive | FilterPrimitive[] | null {
      if (option.operation === FilterOperations.HasValue) {
         return option.value;
      }
      switch (option.type) {
         case 'callParkingLot':
            return Array.isArray(option.value)
               ? option.value.map((v) => {
                    return v.callDetailId;
                 })
               : option.value?.callDetailId || null;
         case 'text-box':
         case 'date':
         case 'numeric':
         case 'boolean':
            return option.value;
      }
   }
}
